import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout/Layout';
import PageHeading from '@/components/common/PageHeading';
import Card from '@/components/UI/Card';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import Pagination from '@/components/common/Pagination';
import ImagePreview from '@/components/common/media/ImagePreview';
import StatusDropdown from '@/components/common/StatusComponent';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import SyncProductModel from '@/components/common/Products/SyncProductModel';
import Model from '@/components/common/models/Model';
import UploadField from '@/components/UI/UploadField';

import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import {
  checkIsExpire,
  GetFilterUrl,
  getStatusWarning,
  productOptions,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';

import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';

import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useListQueryParams } from '@/hooks/useListQueryParams';

import { Star } from 'lucide-react';
import { svgIcons } from '@/components/UI/SvgIcons';
import { RoutesLink } from '@/utility/RouteLinks';
import { getPermissionName } from '@/context/permission';
import ExportOption from '@/components/common/export/ExportOption';

const { RefreshIcon } = svgIcons;

interface ProductStates {
  isLoading: boolean;
  uploadModel: boolean;
  products: IproductResponse[];
  pageInfo: PageInfo;
  showDeleteModel: boolean;
  selectedProductId: string | null;
  showUpdateModel: boolean;
  statusToUpdate: string | null;
}

const statusOptions = [
  ...productOptions,
  { value: 'out_of_stock', label: 'Out of stock' },
];

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canEdit, canDelete } = usePermissions();

  const { params, setParams } = useListQueryParams({
    status: 'active',
    search: '',
    sort: '',
  });

  const { page, limit, search, status, sort } = params;

  const [states, setStates] = useState<ProductStates>({
    isLoading: false,
    uploadModel: false,
    products: [],
    pageInfo: {
      page,
      limit,
      total: 0,
      totalPages: 0,
    },
    showDeleteModel: false,
    selectedProductId: null,
    showUpdateModel: false,
    statusToUpdate: null,
  });

  const [syncModel, setSyncModel] = useState(false);

  const handleChangeState = (payload: Partial<ProductStates>) => {
    setStates((prev) => ({ ...prev, ...payload }));
  };

  // 🔹 Fetch products
  const fetchProducts = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page,
        limit,
        search,
        status,
        sort,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });

      handleChangeState({
        products: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      });
    } catch (err) {
      console.error(err);
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page, limit, search, status, sort]);

  const handleDeleteProduct = async () => {
    if (!states.selectedProductId) return;

    try {
      handleChangeState({ isLoading: true });
      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${states.selectedProductId}`,
        { method: 'DELETE' },
      );

      showToastNotification('success', 'Product deleted successfully');
      setParams({ page: 1 });
    } catch (e: any) {
      showToastNotification('danger', e.message);
    } finally {
      handleChangeState({
        isLoading: false,
        showDeleteModel: false,
        selectedProductId: null,
      });
    }
  };

  const onStatusUpdate = async () => {
    if (!states.selectedProductId || !states.statusToUpdate) return;

    try {
      handleChangeState({ isLoading: true });
      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${states.selectedProductId}`,
        {
          method: 'PATCH',
          data: { status: states.statusToUpdate },
        },
      );
      fetchProducts();
    } catch (e) {
      ThrowError(e);
    } finally {
      handleChangeState({
        isLoading: false,
        showUpdateModel: false,
        selectedProductId: null,
        statusToUpdate: null,
      });
    }
  };

  const syncProducts = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.sync}/products`;
      await callApi(PATH, { method: 'GET' }); // SYNC API CALL
      setSyncModel(false);
      fetchProducts();
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  // 🔹 Table columns
  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.directImageUrl
          ? item?.directImageUrl
          : item?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={productOptions}
          onChange={(status) => {
            handleChangeState({
              showUpdateModel: true,
              selectedProductId: item.id,
              statusToUpdate: status,
            });
          }}
          canEditVal="product"
        />
      ),
    },
    {
      key: 'stock',
      header: 'Unit Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'stock',
      header: 'CTN Stock',
      render: (item) => item.productInfo[0]?.primaryQuantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'ItemCode',
      header: 'Item Code',
      render: (item) => <span>{item?.itemCode || '--'}</span>,
    },
    {
      key: 'ItemId',
      header: 'Item Id',
      render: (item) => <span>{item?.inventoryItemId}</span>,
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item?.averageRating || 0, 1)}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const productActionConfig: Record<string, ActionConfig<IproductResponse>> = {
    view: {
      text: 'View',
      onClick: (p) => navigate(`${RoutesLink.productDetail}/${p.id}`),
      permission: () => canView('product'),
    },
    edit: {
      text: 'Edit',
      onClick: (p) => navigate(`${RoutesLink.addProducts}/${p.id}`),
      permission: () => canEdit('product'),
    },
    delete: {
      text: 'Delete',
      onClick: (p) =>
        handleChangeState({
          showDeleteModel: true,
          selectedProductId: p.id,
        }),
      permission: () => canDelete('product'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(productActionConfig)
        .filter((a) => a.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit, canDelete],
  );

  return (
    <Layout>
      <PageHeading
        text="Products"
        button={
          canEdit(getPermissionName('update:product'))
            ? {
                icon: RefreshIcon,
                text: 'Sync Products',
                onClick: () => setSyncModel(true),
              }
            : undefined
        }
      />

      <Card>
        <div className="flex items-start justify-between gap-3">
          <CommonFilter
            activeFilter={status}
            filters={statusOptions}
            inputPlaceHolder="Search products..."
            onSearch={(text) => setParams({ search: text })}
            onChange={(status) => setParams({ status })}
            searchValue={search}
            filterOptions={{
              values: [
                { value: 'az', label: 'A to Z' },
                { value: 'za', label: 'Z to A' },
              ],
              onChange: (sort) => setParams({ sort }),
            }}
          />

          {states.products.length > 0 && (
            <ExportOption
              APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/export/excel`}
              status={status}
            />
          )}
        </div>
        <CommonTable
          columns={columns}
          data={states.products}
          loading={states.isLoading}
          actions={actions}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
        />

        <Pagination
          paginationData={states.pageInfo}
          onPageChange={(p) => setParams({ page: p })}
          onPageSizeChange={(l) => setParams({ limit: l })}
        />
      </Card>

      {states.showDeleteModel && (
        <ConfirmationModel
          show
          type="product"
          onClose={() =>
            handleChangeState({
              showDeleteModel: false,
              selectedProductId: null,
            })
          }
          onConfirm={handleDeleteProduct}
        />
      )}

      {states.showUpdateModel && (
        <ConfirmationModel
          modelType="update"
          show
          subTitle={`Update status to ${states.statusToUpdate}`}
          warning={
            states.statusToUpdate === 'inactive'
              ? getStatusWarning('product')
              : ''
          }
          onClose={() =>
            handleChangeState({
              showUpdateModel: false,
              selectedProductId: null,
              statusToUpdate: null,
            })
          }
          onConfirm={onStatusUpdate}
        />
      )}

      <SyncProductModel
        show={syncModel}
        onClose={() => setSyncModel(false)}
        onSubmit={() => syncProducts()}
        isLoading={states.isLoading}
      />

      <Model
        show={states.uploadModel}
        title="Select the file/folder"
        onClose={() => handleChangeState({ uploadModel: false })}
      >
        <UploadField
          referenceType="product"
          multiple
          folderUpload
          onChangeMedia={() => handleChangeState({ uploadModel: false })}
        />
      </Model>
    </Layout>
  );
};

export default Products;
